import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CompanyList from '../../components/admin/company/CompanyList';
import CompanyForm from '../../components/admin/company/CompanyForm';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import CredentialsModal from '../../components/admin/company/CredentialsModal';

const CompanyPage = () => {
  const { companies, addCompany, updateCompany, deleteCompany, fetchCompanies } = useData();
  const { isAuthenticated, role, user } = useAuth();
  const navigate = useNavigate();

  const [showForm, setShowForm] = useState(false);
  const [currentCompany, setCurrentCompany] = useState(null);
  const [showCredentials, setShowCredentials] = useState(false);
  const [credentials, setCredentials] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const isFirstRender = useRef(true);
  const refreshIntervalRef = useRef(null);

  // Check if user is authenticated and has admin role
  useEffect(() => {
    if (!isAuthenticated) {
      console.log('User not authenticated, redirecting to login');
      navigate('/login');
      return;
    }

    console.log('Current user role:', role);

    if (role !== 'admin') {
      console.log('User not admin, redirecting to appropriate page');
      navigate('/');
      return;
    }

    console.log('User authenticated as admin:', user);
  }, [isAuthenticated, role, navigate, user]);

  // Memoized function to load companies
  const loadCompanies = useCallback(async (showLoadingState = true) => {
    try {
      if (showLoadingState) {
        setInitialLoading(true);
      } else {
        setIsRefreshing(true);
      }

      await fetchCompanies();
      console.log('Companies loaded successfully');
    } catch (error) {
      console.error('Error loading companies:', error);
      // Only show alert on initial load, not on background refreshes
      if (showLoadingState) {
        alert('Failed to load companies. Please refresh the page.');
      }
    } finally {
      if (showLoadingState) {
        setInitialLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  }, [fetchCompanies]);

  // Initial load of companies
  useEffect(() => {
    if (isFirstRender.current) {
      loadCompanies(true);
      isFirstRender.current = false;
    }
  }, [loadCompanies]);

  // Set up refresh interval
  useEffect(() => {
    // Clear any existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    // Set up a new interval
    refreshIntervalRef.current = setInterval(() => {
      // Only refresh if not already refreshing and not showing the form
      if (!isRefreshing && !showForm) {
        loadCompanies(false);
      }
    }, 30000); // Refresh every 30 seconds instead of 10 to reduce flickering

    // Clean up the interval when the component unmounts
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [loadCompanies, isRefreshing, showForm]);

  const handleAddClick = () => {
    setCurrentCompany(null);
    setShowForm(true);
  };

  const handleEditClick = (company) => {
    setCurrentCompany(company);
    setShowForm(true);
  };

  const handleDeleteClick = async (id) => {
    if (window.confirm('Are you sure you want to delete this company? This will also permanently delete the associated user account.')) {
      // Find the delete button and show loading state on it
      const deleteButton = document.querySelector(`button[data-company-id="${id}"]`);
      if (deleteButton) {
        deleteButton.disabled = true;
        deleteButton.innerHTML = 'Deleting...';
      }

      try {
        const success = await deleteCompany(id);

        if (success) {
          // Refresh the companies list without showing loading state
          await loadCompanies(false);

          // Show success message after state updates
          setTimeout(() => {
            alert('Company and its associated user account have been successfully deleted.');
          }, 100);
        } else {
          throw new Error('Delete operation returned false');
        }
      } catch (error) {
        console.error('Error in handleDeleteClick:', error);

        // Reset the button state
        if (deleteButton) {
          deleteButton.disabled = false;
          deleteButton.innerHTML = 'Delete';
        }

        // Show error message after a short delay
        setTimeout(() => {
          alert('Failed to delete the company. Please try again.');
        }, 100);
      }
    }
  };

  const handleFormSubmit = async (formData) => {
    // Use a local loading state for the form submission
    const formSubmitButton = document.getElementById('companyFormSubmitButton');
    if (formSubmitButton) {
      formSubmitButton.disabled = true;
      formSubmitButton.textContent = 'Saving...';
    }

    try {
      if (currentCompany) {
        // Update existing company
        console.log('Updating company with ID:', currentCompany.id);

        const updatedCompany = await updateCompany(currentCompany.id, formData);
        console.log('Company updated successfully:', updatedCompany);

        setShowForm(false);

        // Refresh companies without showing loading state
        await loadCompanies(false);

        // Show success message after state updates
        setTimeout(() => {
          alert('Company updated successfully!');
        }, 100);
      } else {
        // Add new company
        console.log('Creating new company with data:', formData);

        try {
          const newCompany = await addCompany(formData);
          console.log('Company created successfully:', newCompany);

          setShowForm(false);

          // Refresh companies without showing loading state
          await loadCompanies(false);

          // Check if we have user credentials in the response
          if (newCompany && newCompany.user_credentials) {
            // Show credentials modal after a short delay to avoid UI flicker
            setTimeout(() => {
              // Log the credentials for debugging
              console.log('Company credentials:', newCompany.user_credentials);

              setCredentials({
                username: newCompany.user_credentials.username,
                loginEmail: newCompany.user_credentials.login_email || newCompany.user_credentials.username,
                password: newCompany.user_credentials.password,
                companyName: newCompany.name
              });
              setShowCredentials(true);
            }, 100);
          } else {
            // If no credentials in response, show a generic success message
            setTimeout(() => {
              alert('Company created successfully!');
            }, 100);
          }
        } catch (createError) {
          console.error('Error creating company:', createError);

          // Show detailed error message
          setTimeout(() => {
            if (createError.response && createError.response.data) {
              const errorData = createError.response.data;

              if (errorData.detail) {
                alert(`Error: ${errorData.detail}`);
              } else if (errorData.email) {
                // Special handling for email errors
                alert(`Email Error: ${errorData.email}`);
              } else if (errorData.username) {
                // Special handling for username errors
                alert(`Username Error: ${errorData.username}`);
              } else if (errorData.error) {
                // Special handling for general errors
                alert(`Error: ${errorData.error}`);
              } else if (typeof errorData === 'object') {
                const errorMessages = Object.entries(errorData)
                  .map(([field, value]) => `${field}: ${value}`)
                  .join('\n');
                alert(`Error creating company:\n${errorMessages}`);
              } else {
                alert(`Error: ${JSON.stringify(errorData)}`);
              }
            } else if (createError.message) {
              alert(`Error: ${createError.message}`);
            } else {
              alert('Failed to create company. Please try again.');
            }
          }, 100);

          throw createError; // Re-throw to prevent further processing
        }
      }
    } catch (error) {
      console.error('Error in form submission:', error);
      // Error is already handled in the nested try-catch
    } finally {
      // Reset the button state
      if (formSubmitButton) {
        formSubmitButton.disabled = false;
        formSubmitButton.textContent = currentCompany ? 'Update Company' : 'Add Company';
      }
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
  };

  const handleCloseCredentials = () => {
    setShowCredentials(false);
    setCredentials(null);
  };

  return (
    <div>
      <div style={styles.header}>
        <h1 style={styles.title}>Companies</h1>
        <button
          onClick={handleAddClick}
          style={styles.addButton}
          disabled={initialLoading || isRefreshing}
        >
          Add Company
        </button>
      </div>

      {showForm ? (
        <CompanyForm
          company={currentCompany}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
        />
      ) : (
        <div style={{ position: 'relative' }}>
          {/* Only show loading spinner on initial load when no companies exist */}
          {initialLoading && companies.length === 0 ? (
            <div style={styles.loadingContainer}>
              <div style={styles.loadingSpinner}></div>
              <p>Loading companies...</p>
            </div>
          ) : (
            <>
              <CompanyList
                companies={companies}
                onEdit={handleEditClick}
                onDelete={handleDeleteClick}
              />

              {/* Show a subtle refreshing indicator when refreshing in the background */}
              {isRefreshing && (
                <div style={styles.refreshingIndicator}>
                  <div style={styles.smallSpinner}></div>
                  <span>Refreshing...</span>
                </div>
              )}

              {/* Show empty state if no companies exist */}
              {!initialLoading && companies.length === 0 && (
                <div style={styles.emptyState}>
                  <p>No companies found. Click "Add Company" to create one.</p>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Credentials Modal */}
      {showCredentials && credentials && (
        <CredentialsModal
          credentials={credentials}
          onClose={handleCloseCredentials}
        />
      )}
    </div>
  );
};

const styles = {
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
  },
  title: {
    margin: 0,
    color: '#2c3e50',
  },
  addButton: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#e74c3c',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'background-color 0.3s',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
  },
  loadingSpinner: {
    width: '40px',
    height: '40px',
    border: '4px solid rgba(0, 0, 0, 0.1)',
    borderRadius: '50%',
    borderTop: '4px solid #e74c3c',
    animation: 'spin 1s linear infinite',
    marginBottom: '1rem',
  },
  refreshingIndicator: {
    position: 'absolute',
    top: '10px',
    right: '10px',
    display: 'flex',
    alignItems: 'center',
    padding: '5px 10px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '4px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    fontSize: '0.8rem',
    color: '#666',
    zIndex: 5,
  },
  smallSpinner: {
    width: '12px',
    height: '12px',
    border: '2px solid rgba(0, 0, 0, 0.1)',
    borderRadius: '50%',
    borderTop: '2px solid #e74c3c',
    animation: 'spin 1s linear infinite',
    marginRight: '8px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '3rem',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
    color: '#666',
    textAlign: 'center',
  },
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
};

export default CompanyPage;
